import React, { Fragment, Suspense } from "react";
import { extract } from "./../../../ricos-content/RicosContentAPI/extract-next.js";
import { Node_Type } from "./../../../ricos-schema/next-schema/index.js";
import { getCroppedImage } from "./../../../_shared/media/get-cropped-image.js";
import { normalizeGalleryItems } from "./../../../plugin-gallery/media/normalize-gallery-items.js";
import { isFullscreenExpandableNode, toFullscreenDocument } from "./to-fullscreen-document.js";
const loadFullscreenModal = () => import(
  /* webpackChunkName: "ricos_FullscreenModalViewer"  */
  "./fullscreen-modal.js"
);
const FullscreenModal = React.lazy(loadFullscreenModal);
const FullscreenProvider = (props) => {
  const { children, isMobile, fullscreenProps, content } = props;
  const lastInteractionRef = React.useRef();
  const [state, setState] = React.useState({ type: "closed" });
  React.useEffect(() => {
    const recordInteraction = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      lastInteractionRef.current = target.closest("button, a, [tabindex]") || target;
    };
    document.addEventListener("pointerdown", recordInteraction, true);
    document.addEventListener("keydown", recordInteraction, true);
    return () => {
      document.removeEventListener("pointerdown", recordInteraction, true);
      document.removeEventListener("keydown", recordInteraction, true);
    };
  }, []);
  const expandModeData = React.useMemo(() => {
    if (content) {
      return getExpandModeData(toFullscreenDocument(content));
    }
    return { items: [], itemRanges: {} };
  }, [content]);
  const addExpand = (config) => {
    const onExpand = (nodeId, innerIndex = 0) => {
      const activeIndex = getExpandIndex(expandModeData, nodeId, innerIndex);
      if (activeIndex === void 0) {
        return;
      }
      const activeElement = document.activeElement;
      const lastInteraction = lastInteractionRef.current;
      setState({
        type: "open",
        activeIndex,
        restoreFocusTo: lastInteraction?.isConnected && lastInteraction !== document.body ? lastInteraction : activeElement instanceof HTMLElement ? activeElement : void 0
      });
    };
    const newConfig = { ...config };
    const imageConfig = config["wix-draft-plugin-image"];
    const galleryConfig = config["wix-draft-plugin-gallery"];
    if (imageConfig && !imageConfig.onExpand) {
      newConfig["wix-draft-plugin-image"] = { ...imageConfig, onExpand };
    }
    if (galleryConfig && !galleryConfig.onExpand) {
      newConfig["wix-draft-plugin-gallery"] = { ...galleryConfig, onExpand };
    }
    return newConfig;
  };
  return /* @__PURE__ */ React.createElement(Fragment, null, children({ config: addExpand(props.config), onHover: () => loadFullscreenModal() }), state.type === "open" && /* @__PURE__ */ React.createElement(Suspense, { fallback: /* @__PURE__ */ React.createElement("div", null) }, /* @__PURE__ */ React.createElement(
    FullscreenModal,
    {
      items: expandModeData.items,
      onClose: () => setState({ type: "closed" }),
      index: state.activeIndex,
      restoreFocusTo: state.restoreFocusTo,
      isMobile,
      ...fullscreenProps,
      jsdomMode: props.jsdomMode,
      t: props.t,
      userDomainMedia: props.userDomainMedia
    }
  )));
};
const getExpandModeData = (content) => {
  const items = [];
  const itemRanges = {};
  extract(content.nodes).get().forEach((node, nodeIndex) => {
    if (!isFullscreenExpandableNode(node)) {
      return;
    }
    const nodeKey = node.id || `fullscreen-node:${nodeIndex}`;
    const nodeItems = normalizeNodeItems(node, nodeKey);
    if (node.id) {
      itemRanges[node.id] = { startIndex: items.length, itemCount: nodeItems.length };
    }
    items.push(...nodeItems);
  });
  return { items, itemRanges };
};
const getExpandIndex = (data, nodeId, innerIndex = 0) => {
  const range = data.itemRanges[nodeId];
  if (!range || range.itemCount === 0) {
    return void 0;
  }
  const requestedIndex = Number.isFinite(innerIndex) ? Math.trunc(innerIndex) : 0;
  return range.startIndex + Math.min(Math.max(requestedIndex, 0), range.itemCount - 1);
};
const normalizeNodeItems = (node, nodeKey) => {
  switch (node.type) {
    case Node_Type.IMAGE: {
      if (!node.imageData.image?.src || node.imageData.disableExpand) {
        return [];
      }
      return normalizeGalleryItems({
        items: [
          {
            image: { media: getFullscreenImageMedia(node) },
            altText: node.imageData.altText
          }
        ],
        nodeId: nodeKey,
        disableDownload: node.imageData.disableDownload
      });
    }
    case Node_Type.GALLERY: {
      if (node.galleryData.disableExpand) {
        return [];
      }
      return normalizeGalleryItems({
        items: node.galleryData.items,
        nodeId: nodeKey,
        disableDownload: node.galleryData.disableDownload
      });
    }
  }
};
const getFullscreenImageMedia = (node) => {
  const media = node.imageData.image;
  const croppedImage = getCroppedImage({
    mediaId: media?.src?.id,
    sourceWidth: media?.width,
    sourceHeight: media?.height,
    crop: node.imageData.crop
  });
  return croppedImage ? {
    ...media,
    src: { url: croppedImage.url },
    width: croppedImage.width,
    height: croppedImage.height
  } : { ...media };
};
export {
  FullscreenProvider,
  getExpandIndex,
  getExpandModeData
};
//# sourceMappingURL=fullscreen-provider.js.map
